import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuctionItems } from "@/hooks/useAuctionItems";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ReportSamplePreview from "@/components/feature/ReportSamplePreview";
import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { triggerOpenChatbotWithMessage } from "@/hooks/useChatbotTrigger";
import { buildAnalysisMessage } from "@/utils/auctionHelpers";

const sectionVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (delay: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay, duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const },
  }),
};

export default function AuctionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // 목록과 같은 소스를 봐야 카드 클릭이 404 로 떨어지지 않는다.
  const { items: auctionItems, loading } = useAuctionItems();
  const item = auctionItems.find((a) => a.id === id);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showAnalysisPopup, setShowAnalysisPopup] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [aiTriggered, setAiTriggered] = useState(false);
  const [showCalcDropdown, setShowCalcDropdown] = useState(false);

  const handleAIAnalysis = () => {
    if (!item) return;
    setAiTriggered(true);
    triggerOpenChatbotWithMessage(buildAnalysisMessage(item), true);
    setTimeout(() => setAiTriggered(false), 2000);
  };

  if (!item) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
        <Navbar />
        <main className="pt-24 pb-16 px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-[600px] mx-auto text-center"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-[#C4956A]"></i>
            </div>
            <p className="text-[#5C5650] font-medium mb-1">
              해당 경매 물건을 찾을 수 없습니다
            </p>
            <p className="text-sm text-[#8C8780] mb-6">
              삭제되었거나 존재하지 않는 물건입니다
            </p>
            <Link
              to="/auctions"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1C1917] text-white text-sm font-medium rounded-full hover:bg-[#C4956A] transition-all"
            >
              <i className="ri-arrow-left-line"></i>
              경매 물건 목록으로
            </Link>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  const profitRate =
    Math.round(
      (item.expectedProfit / (item.expectedSalePrice - item.expectedProfit)) *
        100
    ) || 0;

  const daysLeft = Math.ceil(
    (new Date(item.auctionDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          {/* Breadcrumb */}
          <motion.nav
            custom={0}
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="flex items-center gap-2 text-xs text-[#8C8780] mb-6"
          >
            <Link to="/" className="hover:text-[#C4956A] transition-colors">
              홈
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link
              to="/auctions"
              className="hover:text-[#C4956A] transition-colors"
            >
              경매 물건
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-[#5C5650]">{item.caseNumber}</span>
          </motion.nav>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column */}
            <div className="lg:w-3/5">
              {/* Image */}
              <motion.div
                custom={0.05}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="relative rounded-2xl overflow-hidden h-[350px] md:h-[450px] mb-6"
              >
                {item.imageUrl ? (
                  <motion.img loading="lazy" decoding="async"
                    initial={{ scale: 1.05 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" as const }}
                    src={item.imageUrl}
                    alt={`${item.propertyType} - ${item.location}`}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#F5F2EE] to-[#E8E5E0] flex flex-col items-center justify-center gap-3">
                    <i className="ri-building-2-line text-6xl text-[#C4B8A8]"></i>
                    <span className="text-sm text-[#A89C8C]">{item.propertyType} · 사진 준비중</span>
                  </div>
                )}
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span
                    className={`px-3 py-1.5 text-sm font-semibold rounded-full ${
                      item.grade === "A"
                        ? "bg-emerald-500 text-white"
                        : item.grade === "B"
                        ? "bg-amber-500 text-white"
                        : "bg-gray-400 text-white"
                    }`}
                  >
                    {item.grade}등급
                  </span>
                  <span className="px-3 py-1.5 text-sm font-medium bg-white/90 rounded-full text-[#1C1917]">
                    유찰 {item.failureCount}회
                  </span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsFavorite(!isFavorite)}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
                  aria-label="관심물건 저장"
                >
                  <i
                    className={`text-lg ${
                      isFavorite
                        ? "ri-heart-fill text-rose-500"
                        : "ri-heart-line text-[#5C5650]"
                    }`}
                  ></i>
                </motion.button>
              </motion.div>

              {/* Rights Analysis */}
              <motion.div
                custom={0.1}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8 mb-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-[#C4956A]/10 flex items-center justify-center">
                    <i className="ri-file-search-line text-[#C4956A]"></i>
                  </div>
                  <h2 className="text-lg font-bold text-[#1C1917]">권리분석</h2>
                </div>
                <div className="bg-[#FAFAF8] rounded-lg p-4 border border-[#E8E5E0]">
                  <p className="text-sm text-[#5C5650] leading-relaxed">
                    {item.rightsAnalysis}
                  </p>
                </div>
              </motion.div>

              {/* Detailed Info */}
              <motion.div
                custom={0.15}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
              >
                <h2 className="text-lg font-bold text-[#1C1917] mb-4">
                  상세 정보
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#8C8780] mb-1">사건번호</p>
                    <p className="text-sm font-semibold text-[#1C1917]">
                      {item.caseNumber}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8C8780] mb-1">법원</p>
                    <p className="text-sm font-semibold text-[#1C1917]">
                      {item.court}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8C8780] mb-1">소재지</p>
                    <p className="text-sm font-semibold text-[#1C1917]">
                      {item.location}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8C8780] mb-1">물건종류</p>
                    <p className="text-sm font-semibold text-[#1C1917]">
                      {item.propertyType}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8C8780] mb-1">임차인</p>
                    <p className="text-sm font-semibold text-[#1C1917]">
                      {item.hasTenant ? "있음" : "없음"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-[#8C8780] mb-1">유찰횟수</p>
                    <p className="text-sm font-semibold text-[#1C1917]">
                      {item.failureCount}회
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Sticky Sidebar */}
            <div className="lg:w-2/5">
              <div className="lg:sticky lg:top-24 space-y-5">
                {/* Price Card */}
                <motion.div
                  custom={0.08}
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  className="bg-white rounded-xl border border-[#E8E5E0] p-6 md:p-8"
                >
                  <h1 className="text-xl font-bold text-[#1C1917] mb-1">
                    {item.location}
                  </h1>
                  <p className="text-sm text-[#8C8780] mb-6">
                    {item.propertyType} · {item.court}
                  </p>

                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center py-2 border-b border-[#F0EDE8]">
                      <span className="text-sm text-[#8C8780]">감정가</span>
                      <span className="text-sm font-semibold text-[#1C1917]">
                        {item.appraisedValue.toLocaleString()}원
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#F0EDE8]">
                      <span className="text-sm text-[#8C8780]">최저가</span>
                      <span className="text-lg font-bold text-[#C4956A]">
                        {item.minimumPrice.toLocaleString()}원
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-[#F0EDE8]">
                      <span className="text-sm text-[#8C8780]">예상매도가</span>
                      <span className="text-sm font-semibold text-[#1C1917]">
                        {item.expectedSalePrice.toLocaleString()}원
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="text-sm text-[#8C8780]">예상수익</span>
                      <div className="text-right">
                        <span className="text-sm font-bold text-emerald-600">
                          {item.expectedProfit.toLocaleString()}원
                        </span>
                        <span className="text-xs text-emerald-500 ml-1">
                          ({profitRate}%)
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Auction Date Badge */}
                  <div className="bg-[#FAFAF8] rounded-lg p-4 mb-6 border border-[#E8E5E0]">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-[#8C8780] mb-0.5">입찰일</p>
                        <p className="text-base font-bold text-[#1C1917]">
                          {item.auctionDate}
                        </p>
                      </div>
                      {daysLeft > 0 && (
                        <div className="text-right">
                          <p className="text-xs text-[#8C8780] mb-0.5">남은기간</p>
                          <p className="text-base font-bold text-rose-500">
                            D-{daysLeft}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">

                    {/* ★ AI 권리분석 — 가장 핵심 CTA */}
                    <motion.button
                      whileHover={{ scale: 1.02, boxShadow: '0 8px 25px rgba(196,149,106,0.4)' }}
                      whileTap={{ scale: 0.97 }}
                      onClick={handleAIAnalysis}
                      disabled={aiTriggered}
                      className="w-full flex items-center gap-3 px-5 py-4 rounded-xl text-left transition-all whitespace-nowrap cursor-pointer disabled:opacity-75 disabled:cursor-not-allowed overflow-hidden relative"
                      style={{
                        background: 'linear-gradient(135deg, #1C1917 0%, #3D2315 55%, #C4956A 100%)',
                        boxShadow: '0 4px 16px rgba(196,149,106,0.25)',
                      }}
                    >
                      {/* 빛나는 shimmer 효과 */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500" />

                      <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                        {aiTriggered ? (
                          <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        ) : (
                          <i className="ri-robot-2-line text-xl text-white"></i>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block text-sm font-bold text-white">
                          {aiTriggered ? 'AI 분석 시작 중...' : '이 물건 AI 분석 시작'}
                        </span>
                        <span className="block text-xs text-white/60">
                          원클릭 · 권리분석 + 배당 시뮬레이션
                        </span>
                      </div>
                      <i className="ri-sparkling-line text-[#C4956A] text-lg shrink-0"></i>
                    </motion.button>

                    {/* 이 물건으로 계산하기 */}
                    <div className="relative">
                      <motion.button
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowCalcDropdown((v) => !v)}
                        className="w-full flex items-center justify-between px-5 py-3 rounded-xl border-2 border-[#C4956A]/40 bg-[#C4956A]/5 hover:bg-[#C4956A]/10 hover:border-[#C4956A]/60 transition-all whitespace-nowrap cursor-pointer"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg bg-[#C4956A]/15 flex items-center justify-center">
                            <i className="ri-calculator-line text-[#C4956A] text-base"></i>
                          </div>
                          <div className="text-left">
                            <span className="block text-sm font-bold text-[#1C1917]">이 물건으로 계산하기</span>
                            <span className="block text-xs text-[#8C8780]">일반방식 · 시세역산방식</span>
                          </div>
                        </div>
                        {showCalcDropdown
                          ? <i className="ri-arrow-up-s-line text-[#C4956A]"></i>
                          : <i className="ri-arrow-down-s-line text-[#C4956A]"></i>
                        }
                      </motion.button>
                      <AnimatePresence>
                        {showCalcDropdown && (
                          <motion.div
                            initial={{ opacity: 0, y: -8, scaleY: 0.9 }}
                            animate={{ opacity: 1, y: 0, scaleY: 1 }}
                            exit={{ opacity: 0, y: -8, scaleY: 0.9 }}
                            transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] as const }}
                            style={{ transformOrigin: "top" }}
                            className="mt-2 bg-white border border-[#E8E5E0] rounded-xl overflow-hidden"
                          >
                            <Link
                              to={`/bid-calculator?itemId=${item.id}&mode=normal`}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAF8] transition-colors group"
                            >
                              <div className="w-8 h-8 rounded-lg bg-[#1C1917]/8 flex items-center justify-center">
                                <i className="ri-calculator-fill text-[#1C1917] text-sm"></i>
                              </div>
                              <div>
                                <span className="block text-xs font-bold text-[#1C1917]">일반방식</span>
                                <span className="block text-[10px] text-[#8C8780]">매각시세 기준 3단계 추천가</span>
                              </div>
                              <i className="ri-arrow-right-line text-[#B8B3AE] ml-auto group-hover:text-[#C4956A] transition-colors"></i>
                            </Link>
                            <div className="border-t border-[#F0EDE8]"></div>
                            <Link
                              to={`/bid-calculator?itemId=${item.id}&mode=reverse`}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-[#FAFAF8] transition-colors group"
                            >
                              <div className="w-8 h-8 rounded-lg bg-[#C4956A]/10 flex items-center justify-center">
                                <i className="ri-flashlight-fill text-[#C4956A] text-sm"></i>
                              </div>
                              <div>
                                <span className="block text-xs font-bold text-[#1C1917]">시세역산방식</span>
                                <span className="block text-[10px] text-[#8C8780]">급매가 기준 투찰 상한가 역산</span>
                              </div>
                              <i className="ri-arrow-right-line text-[#B8B3AE] ml-auto group-hover:text-[#C4956A] transition-colors"></i>
                            </Link>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsFavorite(!isFavorite)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-full border-2 text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
                      style={{
                        borderColor: isFavorite ? "#C4956A" : "#E8E5E0",
                        color: isFavorite ? "#C4956A" : "#5C5650",
                      }}
                    >
                      <i
                        className={
                          isFavorite
                            ? "ri-heart-fill text-rose-500"
                            : "ri-heart-line"
                        }
                      ></i>
                      {isFavorite ? "관심물건 등록됨" : "관심물건으로 저장하기"}
                    </motion.button>

                    <div className="grid grid-cols-2 gap-2">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => setShowAnalysisPopup(true)}
                        className="flex items-center justify-center gap-1.5 px-4 py-3 bg-[#C4956A] text-white rounded-full text-xs font-semibold hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-file-search-line"></i>
                        물건분석 신청
                      </motion.button>
                      <Link
                        to={`/consultation?type=analysis&item=${item.id}`}
                        className="flex items-center justify-center gap-1.5 px-4 py-3 bg-[#1C1917] text-white rounded-full text-xs font-semibold hover:bg-[#C4956A] transition-all whitespace-nowrap"
                      >
                        <i className="ri-customer-service-2-line"></i>
                        1:1 상담 신청
                      </Link>
                    </div>
                  </div>
                </motion.div>

                {/* Related Info Tips */}
                <motion.div
                  custom={0.12}
                  variants={sectionVariants}
                  initial="hidden"
                  animate="visible"
                  className="bg-white rounded-xl border border-[#E8E5E0] p-6"
                >
                  <h3 className="text-sm font-bold text-[#1C1917] mb-3 flex items-center gap-2">
                    <i className="ri-lightbulb-line text-[#C4956A]"></i>
                    입찰 전 체크 포인트
                  </h3>
                  <ul className="space-y-2 text-xs text-[#5C5650]">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span>
                        등기부등본 열람하여 권리관계 재확인
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span>
                        현장 방문하여 실제 상태 확인 필수
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span>
                        입찰보증금(최저가의 10%) 준비 여부 확인
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span>
                        인근 시세와 비교하여 적정 입찰가 산정
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-line text-emerald-500 mt-0.5"></i>
                      <span>
                        임차인 배당요구 및 명도 가능성 검토
                      </span>
                    </li>
                  </ul>

                  {/* 낙찰인사이트 연동 */}
                  <div className="mt-5 pt-5 border-t border-[#F0EDE8]">
                    <Link
                      to="/awarded-insights"
                      className="flex items-center gap-3 p-3 rounded-lg bg-[#C4956A]/5 hover:bg-[#C4956A]/10 transition-colors group"
                    >
                      <div className="w-8 h-8 rounded-lg bg-[#C4956A]/15 flex items-center justify-center flex-shrink-0">
                        <i className="ri-history-line text-[#C4956A] text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="block text-xs font-bold text-[#1C1917]">낙찰물건 인사이트</span>
                        <span className="block text-[10px] text-[#8C8780]">실제 낙찰 사례로 미리 배워보세요</span>
                      </div>
                      <i className="ri-arrow-right-line text-[#C4956A] group-hover:translate-x-1 transition-transform"></i>
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Analysis Popup */}
        <AnimatePresence>
          {showAnalysisPopup && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 bg-black/50"
                onClick={() => setShowAnalysisPopup(false)}
              ></motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const }}
                className="relative bg-white rounded-2xl p-6 md:p-8 max-w-[480px] w-full"
              >
                <button
                  onClick={() => setShowAnalysisPopup(false)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-[#8C8780] hover:bg-[#FAFAF8] transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
                <div className="w-12 h-12 rounded-xl bg-[#C4956A]/10 flex items-center justify-center mb-4">
                  <i className="ri-file-search-line text-xl text-[#C4956A]"></i>
                </div>
                <h3 className="text-lg font-bold text-[#1C1917] mb-2">
                  물건분석 신청
                </h3>
                <p className="text-sm text-[#8C8780] mb-6">
                  <strong className="text-[#5C5650]">{item.caseNumber}</strong>{" "}
                  ({item.location})에 대한 전문가 물건분석을 신청하시겠습니까?
                  분석 리포트는 영업일 기준 2-3일 내에 제공됩니다.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowAnalysisPopup(false)}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-[#5C5650] border border-[#E8E5E0] rounded-full hover:bg-[#FAFAF8] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    취소
                  </button>
                  <Link
                    to={`/reports?type=item&item=${item.id}`}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold bg-[#C4956A] text-white rounded-full hover:bg-[#B08050] transition-all whitespace-nowrap text-center"
                  >
                    신청하기
                  </Link>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>

        {/* Fixed Bottom Banner - 실전반 유도 */}
        <AnimatePresence>
          {showBanner && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-[#E8E5E0]"
            >
              <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-4 flex-col sm:flex-row">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-[#C4956A]/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-graduation-cap-line text-lg text-[#C4956A]"></i>
                  </div>
                  <p className="text-sm md:text-base font-medium text-[#1C1917] leading-snug">
                    이 물건을 안전하게 입찰하는 방법,{" "}
                    <span className="font-bold text-[#C4956A] whitespace-nowrap">강스터디 실전반</span>
                    에서 현직 강사와 함께 분석해 드립니다
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setShowReportPreview(true)}
                    className="px-5 py-2 md:px-6 md:py-2.5 bg-white border-2 border-[#C4956A] text-[#C4956A] text-sm font-semibold rounded-full hover:bg-[#C4956A]/5 transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-eye-line"></i>
                    리포트 샘플 보기
                  </button>
                  <Link
                    to="/courses"
                    className="px-5 py-2 md:px-6 md:py-2.5 bg-[#C4956A] text-white text-sm font-semibold rounded-full hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-line"></i>
                    교육과정 바로가기
                  </Link>
                  <button
                    onClick={() => setShowBanner(false)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-[#8C8780] hover:bg-[#FAFAF8] hover:text-[#5C5650] transition-colors cursor-pointer flex-shrink-0"
                    aria-label="배너 닫기"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      <ReportSamplePreview isOpen={showReportPreview} onClose={() => setShowReportPreview(false)} />
      <Footer />
    </div>
  );
}